import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { getCompletedSessions } from "../utils/api";
import "./EmployeeCompletedExams.css";

function EmployeeCompletedExams() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [completedExams, setCompletedExams] = useState([]);
  const [error, setError] = useState("");

  const [searchName, setSearchName] = useState("");
  const [selectedExam, setSelectedExam] = useState("");
  const [selectedCareHome, setSelectedCareHome] = useState("");

  useEffect(() => {
    if (!user || (user.role !== "manager" && user.role !== "group_manager")) {
      setError("Access Denied");
      return;
    }

    const fetchCompletedExams = async () => {
      try {
        const data = await getCompletedSessions(token);
        let filteredData = data;

        // Apply care home filtering for managers
        if (user.role === "manager") {
          filteredData = data.filter(
            (session) => session.care_home === user.carehome
          );
        }

        setCompletedExams(filteredData);
      } catch (err) {
        console.error(err);
        setError("An error occurred while fetching completed exams.");
      }
    };

    fetchCompletedExams();
  }, [user, token]);

  const examNames = useMemo(() => {
    const names = completedExams.map((s) => s.exam_name || s.exam_title);
    return Array.from(new Set(names));
  }, [completedExams]);

  const careHomes = useMemo(() => {
    const homes = completedExams.map((s) => s.care_home).filter(Boolean);
    return Array.from(new Set(homes));
  }, [completedExams]);

  const filteredSessions = useMemo(() => {
    return completedExams.filter((session) => {
      const employeeName = session.employee_name || session.user_name || "";
      const examName = session.exam_name || session.exam_title || "";
      const careHome = session.care_home || "";

      const matchesName = employeeName
        .toLowerCase()
        .includes(searchName.toLowerCase());
      const matchesExam =
        !selectedExam || selectedExam === examName || selectedExam === "All";
      const matchesHome =
        !selectedCareHome ||
        selectedCareHome === careHome ||
        selectedCareHome === "All";

      return matchesName && matchesExam && matchesHome;
    });
  }, [completedExams, searchName, selectedExam, selectedCareHome]);

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="employee-completed-container">
      <h1>Completed Exams</h1>

      <div className="filters-row">
        <label className="filter-label">
          <span>Search by Employee Name:</span>
          <input
            className="filter-input"
            type="text"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            placeholder="Enter partial name..."
          />
        </label>

        <label className="filter-label">
          <span>Filter by Exam:</span>
          <select
            className="filter-select"
            value={selectedExam}
            onChange={(e) => setSelectedExam(e.target.value)}
          >
            <option value="">All Exams</option>
            {examNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>

        <label className="filter-label">
          <span>Filter by Care Home:</span>
          <select
            className="filter-select"
            value={selectedCareHome}
            onChange={(e) => setSelectedCareHome(e.target.value)}
          >
            <option value="">All Care Homes</option>
            {careHomes.map((home) => (
              <option key={home} value={home}>
                {home}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="userlist-table-wrapper">
        <table className="user-table">
          <thead>
            <tr>
              <th>Employee Name</th>
              <th>Exam Name</th>
              <th>Selected Courses</th>
              <th>Score</th>
              <th>Completed Date</th>
              <th>Care Home</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredSessions.map((session) => (
              <tr key={session.id}>
                <td>{session.employee_name || session.user_name}</td>
                <td>{session.exam_name || session.exam_title}</td>
                <td>
                  {Array.isArray(session.selected_courses)
                    ? session.selected_courses.join(", ")
                    : session.selected_courses}
                </td>
                <td>{session.score}%</td>
                <td>
                  {session.end_time
                    ? new Date(session.end_time).toLocaleString()
                    : ""}
                </td>
                <td>{session.care_home || ""}</td>
                <td>
                  <button
                    className="start-btn"
                    onClick={() =>
                      navigate("/dashboard/completed-exams", {
                        state: {
                          sessionId: session.id,
                          userId: session.user_id,
                        },
                      })
                    }
                  >
                    View Exam
                  </button>
                </td>
              </tr>
            ))}
            {filteredSessions.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: "1rem" }}>
                  No matching records.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default EmployeeCompletedExams;
